import { useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import useSWR, { mutate } from "swr";
import { swrKey } from "@/lib/swr";
import { CopyShortId } from "@/components/prds/copy-short-id";
import BackToPoolButton from "@/components/engagements/back-to-pool-button";
import { STATE_PLANNING } from "@/constants/version-state";
import { EngagementDetailSheet } from "./detail-sheet";
import { EngagementMembers, EngagementOwner } from "./cells";
import { CompletionProgress } from "@/components/engagements/completion-progress";
import { MyEngagementStatus } from "@/components/engagements/my-completion";
import { PriorityChip } from "@/components/engagements/priority-chip";
import { RowCard } from "@/components/list/row-card";
import { ListEmpty, ListLoading } from "@/components/list/status-text";
import type { Engagement } from "@/types/domain";

// Short ID + priority + doc name, all off the /engagements row (no per-row
// PRD fetch). Same order as a wish-pool row. Plain text: the row click opens
// the PRD in the sheet.
const EngagementDoc = ({ engagement }: { engagement: Engagement }) => (
  <div className="flex min-w-0 flex-1 items-center gap-2">
    <CopyShortId shortId={engagement.shortId} />
    <PriorityChip engagement={engagement} />
    <span className="min-w-0 flex-1 truncate">{engagement.prd?.docName}</span>
  </div>
);


// One linked PRD as a read-only row, laid out like a wish-pool row: completion
// progress first, then short ID / priority / name, then members and owner,
// plus my own status. No index and no New/Merge state (WED-262). Click opens
// the PRD sheet — every other operation lives there (放回需求池 excepted:
// pre-testing quick exit).
const EngagementCard = ({
  engagement,
  onOpen,
  showBackToPool,
  showMyStatus,
}: {
  engagement: Engagement;
  onOpen: () => void;
  showBackToPool?: boolean;
  showMyStatus?: boolean;
}) => (
  <RowCard onClick={onOpen}>
    {/* Phones drop the completion circle to save room, as on wish-pool. */}
    <div className="hidden md:contents">
      <CompletionProgress engagement={engagement} spin />
    </div>

    <EngagementDoc engagement={engagement} />

    <span className="hidden w-24 shrink-0 justify-center md:flex">
      <EngagementMembers engagement={engagement} />
    </span>

    {/* w-20 hugs a 2-4 char name + avatar; w-28 left a visible blank
        stripe at the row's right edge. Longer names truncate (title tooltip). */}
    <span className="hidden w-20 shrink-0 md:flex">
      <EngagementOwner engagement={engagement} />
    </span>

    {/* My own status (WED-195); blank for non-members. Everyone's status
        lives in the sheet's people rail. The column only exists when some
        row is mine (WED-216), so non-members get no empty stripe. */}
    {showMyStatus && (
      <span className="hidden w-32 shrink-0 justify-end md:flex">
        <MyEngagementStatus engagement={engagement} prdId={engagement.prdId} remind={false} size="xs" />
      </span>
    )}

    {showBackToPool && (
      <BackToPoolButton
        engagementId={engagement.id}
        onDone={() => {
          mutate(swrKey("/engagements", { versionId: engagement.versionId }));
          mutate(swrKey("/engagements", { prdId: engagement.prdId }));
        }}
      />
    )}
  </RowCard>
);

// Column title for the members column — mirrors EngagementCard's gaps/widths
// so the header lines up with the cells. The other columns (progress icon,
// doc, owner) are self-evident and stay unlabeled.
const EngagementListHeader = ({ showMyStatus }: { showMyStatus: boolean }) => (
  <div className="hidden items-center gap-3 px-3 pb-1 text-xs text-muted-foreground md:flex">
    <span className="w-4 shrink-0" />
    <span className="min-w-0 flex-1" />
    <span className="w-24 shrink-0 text-center">Members</span>
    <span className="hidden w-20 shrink-0 md:block" />
    {showMyStatus && <span className="hidden w-32 shrink-0 md:block" />}
  </div>
);

const Engagements = () => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: engagements } = useSWR<Engagement[]>(swrKey("/engagements", { versionId: id }));

  const items = useMemo(() => {
    if (!engagements) return [];

    return [...engagements].sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));
  }, [engagements]);

  // Panel selection is URL-addressable: ?engagement=<id> (shareable, survives
  // reload).
  // Same rule as MyEngagementStatus (remind off): a control only where I can complete.
  const showMyStatus = items.some((e) => e.canComplete);

  const selectedId = searchParams.get("engagement");
  const selected = items.find((p) => p.id === selectedId);

  const openPanel = (engagementId: string) => {
    setSearchParams((prev) => {
      prev.set("engagement", engagementId);
      return prev;
    });
  };
  const closePanel = () => {
    setSearchParams((prev) => {
      prev.delete("engagement");
      return prev;
    });
  };

  return (
    <div className="flex flex-col gap-1">
      {!engagements && <ListLoading />}
      {engagements && items.length === 0 && <ListEmpty>No PRDs.</ListEmpty>}
      {items.length > 0 && <EngagementListHeader showMyStatus={showMyStatus} />}
      {items.map((engagement) => (
        <EngagementCard
          key={engagement.id}
          engagement={engagement}
          onOpen={() => openPanel(engagement.id)}
          showBackToPool={engagement.versionState === STATE_PLANNING}
          showMyStatus={showMyStatus}
        />
      ))}
      {selected && (
        <EngagementDetailSheet
          engagement={selected}
          open
          onOpenChange={(open) => {
            if (!open) closePanel();
          }}
        />
      )}
    </div>
  );
};

export default Engagements;
